import { useState, useMemo, useCallback, useRef, useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { FunnelIcon } from '@heroicons/react/20/solid'
import { FormSelect } from '@/components/ui/form'

// ─── Types ──────────────────────────────────────

export interface DataTableColumn<T> {
  key: string
  label: string
  render: (row: T) => ReactNode
  sortValue?: (row: T) => string | number | null
  filterValue?: (row: T) => string
  /** Hide filter icon for this column */
  noFilter?: boolean
  /** If true, click on cell opens auto-open FormSelect */
  editSelect?: {
    options: { value: string; label: string }[]
    getValue: (row: T) => string
    onSelect: (row: T, value: string) => void
  }
}

interface DataTableProps<T> {
  rows: T[]
  columns: DataTableColumn<T>[]
  rowKey: (row: T) => string | number
  flashRowId?: number | null
  flashStatus?: 'success' | 'error' | null
  emptyText?: string
  filteredEmptyText?: string
  actions?: (row: T) => ReactNode
  /** Optional summary footer row — receives the filtered+sorted rows so
   *  consumers can compute aggregates against what the user is actually
   *  looking at. Renders as a sticky-styled tfoot with one cell per
   *  visible column (the cell-renderer decides whether to show a value). */
  summary?: (visibleRows: T[]) => ReactNode
  /** Notify consumer whenever the filtered+sorted row set changes (so
   *  side-effects like Excel export can target the same rows the user
   *  sees). */
  onVisibleRowsChange?: (visibleRows: T[]) => void
  /** Switch from CSS `table-layout: auto` (the default — columns
   *  squeeze to fit the parent) to `table-fixed` with explicit widths
   *  from <colgroup>. When set, each header gets a resize handle and
   *  cell content truncates with ellipsis on overflow. */
  resizable?: boolean
  /** Per-column starting widths in pixels. Keys must match column keys.
   *  Required when `resizable` is true. */
  widths?: Record<string, number>
  /** localStorage key — when set, the resized widths persist per browser
   *  across reloads. */
  widthsStorageKey?: string
}

// ─── Component ──────────────────────────────────

export default function DataTable<T>({ rows, columns, rowKey, flashRowId, flashStatus, emptyText = 'No data', filteredEmptyText = 'No data matches your filters', actions, summary, onVisibleRowsChange, resizable, widths, widthsStorageKey }: DataTableProps<T>) {
  const [columnFilters, setColumnFilters] = useState<Record<string, Set<string>>>({})
  const [activeFilter, setActiveFilter] = useState<string | null>(null)
  const [sortCol, setSortCol] = useState<string>('')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [editingCell, setEditingCell] = useState<{ rowKey: string | number; colKey: string } | null>(null)
  const filterBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  // ─── Column widths (resizable mode) ─────────
  // Hydrate from localStorage on mount; persist on every change. Falls
  // back to the `widths` prop defaults when the saved blob doesn't carry
  // a key yet. 30px floor keeps a column from collapsing to zero.
  const MIN_W = 30
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    if (!resizable) return {}
    let saved: Record<string, number> = {}
    if (widthsStorageKey && typeof window !== 'undefined') {
      try { saved = JSON.parse(window.localStorage.getItem(widthsStorageKey) || '{}') } catch { saved = {} }
    }
    return { ...(widths || {}), ...saved }
  })
  useEffect(() => {
    if (!resizable || !widthsStorageKey || typeof window === 'undefined') return
    window.localStorage.setItem(widthsStorageKey, JSON.stringify(columnWidths))
  }, [resizable, widthsStorageKey, columnWidths])

  const resizingRef = useRef<{ col: string; startX: number; startW: number } | null>(null)
  const startResize = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation()
    const startW = columnWidths[col] ?? widths?.[col] ?? 120
    resizingRef.current = { col, startX: e.clientX, startW }
    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return
      const { col: c, startX, startW: sw } = resizingRef.current
      const nw = Math.max(MIN_W, sw + ev.clientX - startX)
      setColumnWidths(prev => ({ ...prev, [c]: nw }))
    }
    const onUp = () => {
      resizingRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [columnWidths, widths])
  const colWidth = (key: string) => columnWidths[key] ?? widths?.[key] ?? 120

  // ─── Column filter helpers ──────────────────
  const toggleFilter = useCallback((col: string, val: string) => {
    setColumnFilters(prev => {
      const s = prev[col] ? new Set(prev[col]) : new Set<string>()
      if (s.has(val)) s.delete(val); else s.add(val)
      if (s.size === 0) { const n = { ...prev }; delete n[col]; return n }
      return { ...prev, [col]: s }
    })
  }, [])

  const clearFilter = useCallback((col: string) => {
    setColumnFilters(prev => { const n = { ...prev }; delete n[col]; return n })
  }, [])

  const getUniqueValues = useCallback((col: DataTableColumn<T>): string[] => {
    const vals = new Set<string>()
    rows.forEach(r => {
      const v = col.filterValue ? col.filterValue(r) : col.sortValue ? String(col.sortValue(r) ?? '') : ''
      if (v) vals.add(v)
    })
    return [...vals].sort()
  }, [rows])

  // ─── Filter + sort ──────────────────────────
  const activeFilterCount = Object.values(columnFilters).reduce((s, v) => s + v.size, 0)

  const filteredRows = useMemo(() => {
    let result = [...rows]

    for (const [colKey, values] of Object.entries(columnFilters)) {
      if (values.size === 0) continue
      const col = columns.find(c => c.key === colKey)
      if (!col) continue
      result = result.filter(r => {
        const v = col.filterValue ? col.filterValue(r) : col.sortValue ? String(col.sortValue(r) ?? '') : ''
        return values.has(v)
      })
    }

    if (sortCol) {
      const col = columns.find(c => c.key === sortCol)
      if (col) {
        const dir = sortDir === 'asc' ? 1 : -1
        result.sort((a, b) => {
          const va = col.sortValue ? col.sortValue(a) : null
          const vb = col.sortValue ? col.sortValue(b) : null
          if (va == null && vb == null) return 0
          if (va == null) return 1
          if (vb == null) return -1
          if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
          return String(va).localeCompare(String(vb)) * dir
        })
      }
    }

    return result
  }, [rows, columnFilters, sortCol, sortDir, columns])

  // Notify consumer whenever the visible row set changes — used by
  // pages that export "what the user is looking at" rather than the raw
  // input rows (e.g. Vpo/New's Excel export).
  useEffect(() => {
    onVisibleRowsChange?.(filteredRows)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows])

  // ─── Sort toggle ────────────────────────────
  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  // ─── Render ─────────────────────────────────
  if (filteredRows.length === 0) {
    return <div className="py-20 text-center text-zinc-400" data-testid="empty-state">{activeFilterCount > 0 ? filteredEmptyText : emptyText}</div>
  }

  return (
    <div className="overflow-x-auto" data-testid="data-table">
      <table
        className={clsx(
          'text-left text-sm text-zinc-900 dark:text-zinc-100 [&>tbody>tr>td]:truncate',
          resizable ? 'table-fixed' : 'w-full',
        )}
        style={resizable
          ? { width: columns.reduce((sum, c) => sum + colWidth(c.key), 0) + (actions ? 100 : 0) }
          : undefined}
      >
        {resizable && (
          <colgroup>
            {columns.map(c => <col key={c.key} style={{ width: colWidth(c.key) }} />)}
            {actions && <col style={{ width: 100 }} />}
          </colgroup>
        )}
        <thead>
          <tr className="border-b border-zinc-200 text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
            {columns.map(col => {
              const hasFilter = columnFilters[col.key]?.size > 0
              const showFilter = !col.noFilter && col.filterValue
              return (
                <th
                  key={col.key}
                  className={clsx(
                    'relative px-3 py-2',
                    resizable && 'overflow-hidden',
                  )}
                >
                  <span className={clsx('inline-flex min-h-5 max-w-full items-center gap-1', resizable && 'min-w-0')}>
                    {showFilter && (
                      <button
                        ref={el => { filterBtnRefs.current[col.key] = el }}
                        onClick={() => setActiveFilter(activeFilter === col.key ? null : col.key)}
                        className={clsx('rounded p-0.5', hasFilter ? 'text-indigo-600 dark:text-indigo-400' : 'text-zinc-300 hover:text-zinc-500 dark:text-zinc-600 dark:hover:text-zinc-400')}
                        title="Filter"
                      >
                        <FunnelIcon className="h-3 w-3" />
                        {hasFilter && <span className="text-[9px] font-semibold">{columnFilters[col.key].size}</span>}
                      </button>
                    )}
                    <span className={clsx('cursor-pointer hover:text-zinc-700 dark:hover:text-zinc-300', resizable && 'min-w-0 truncate')} onClick={() => col.sortValue ? toggleSort(col.key) : undefined}>
                      {col.label}
                      {sortCol === col.key && <span className="ml-0.5 text-indigo-500">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                    </span>
                  </span>
                  {activeFilter === col.key && (
                    <FilterDropdown
                      colName={col.key}
                      values={getUniqueValues(col)}
                      selected={columnFilters[col.key] || new Set()}
                      onToggle={toggleFilter}
                      onClear={() => clearFilter(col.key)}
                      onClose={() => setActiveFilter(null)}
                      anchorRef={filterBtnRefs.current[col.key]}
                    />
                  )}
                  {resizable && (
                    <div
                      onMouseDown={(e) => startResize(col.key, e)}
                      title="Drag to resize"
                      data-testid={`resize-${col.key}`}
                      className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50"
                    />
                  )}
                </th>
              )
            })}
            {actions && <th className="px-3 py-2 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {filteredRows.map(row => {
            const key = rowKey(row)
            const isFlash = flashRowId === key
            return (
              <tr
                key={key}
                className={clsx(
                  'transition-colors duration-700',
                  isFlash && flashStatus === 'success' && 'bg-emerald-100 dark:bg-emerald-500/15',
                  isFlash && flashStatus === 'error' && 'bg-red-100 dark:bg-red-500/15',
                  !isFlash && 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
                )}
              >
                {columns.map(col => {
                  const isEditing = editingCell?.rowKey === key && editingCell?.colKey === col.key
                  if (col.editSelect && isEditing) {
                    return (
                      <td key={col.key} className="whitespace-nowrap px-3 py-2">
                        <AutoOpenSelect
                          value={col.editSelect.getValue(row)}
                          options={col.editSelect.options}
                          onSelect={v => { col.editSelect!.onSelect(row, v); setEditingCell(null) }}
                          onClose={() => setEditingCell(null)}
                        />
                      </td>
                    )
                  }
                  return (
                    <td
                      key={col.key}
                      className={clsx('whitespace-nowrap px-3 py-2', col.editSelect && 'cursor-pointer')}
                      onClick={col.editSelect ? () => setEditingCell({ rowKey: key, colKey: col.key }) : undefined}
                    >
                      {col.render(row)}
                    </td>
                  )
                })}
                {actions && <td className="whitespace-nowrap px-3 py-2 text-right">{actions(row)}</td>}
              </tr>
            )
          })}
        </tbody>
        {summary && (
          <tfoot className="bg-zinc-50 dark:bg-zinc-800/30">
            {summary(filteredRows)}
          </tfoot>
        )}
      </table>
    </div>
  )
}

// ─── Exported helpers ───────────────────────────

export { type DataTableProps }

export function useFlash() {
  const [flashRow, setFlashRow] = useState<{ id: number; status: 'success' | 'error' } | null>(null)
  const flash = useCallback((id: number, status: 'success' | 'error') => {
    setFlashRow({ id, status })
    setTimeout(() => setFlashRow(null), 700)
  }, [])
  return { flashRow, flash }
}

export function usePagination<T>(rows: T[], defaultSize = 50) {
  const [pageSize, setPageSize] = useState(defaultSize)
  const [currentPage, setCurrentPage] = useState(0)
  const totalPages = Math.ceil(rows.length / pageSize)
  const paginatedRows = useMemo(() => rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize), [rows, currentPage, pageSize])

  const resetPage = useCallback(() => setCurrentPage(0), [])

  return { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, resetPage, totalCount: rows.length }
}

// ─── Auto-Open Select ───────────────────────────

function AutoOpenSelect({ value, options, onSelect, onClose }: {
  value: string; options: { value: string; label: string }[]
  onSelect: (v: string) => void; onClose: () => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    requestAnimationFrame(() => { el.querySelector('button')?.click() })
    const observer = new MutationObserver(() => {
      if (!el.querySelector('[data-open]')) onCloseRef.current()
    })
    const timer = setTimeout(() => observer.observe(el, { attributes: true, attributeFilter: ['data-open'], subtree: true }), 150)
    return () => { clearTimeout(timer); observer.disconnect() }
  }, [])

  return (
    <div ref={containerRef} className="w-32" data-testid="state-edit" onClick={e => e.stopPropagation()}>
      <FormSelect value={value} onChange={onSelect} options={options} />
    </div>
  )
}

// ─── Filter Dropdown (portal) ───────────────────

function FilterDropdown({ colName, values, selected, onToggle, onClear, onClose, anchorRef }: {
  colName: string; values: string[]; selected: Set<string>
  onToggle: (col: string, val: string) => void; onClear: () => void; onClose: () => void
  anchorRef: HTMLButtonElement | null
}) {
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const filtered = search ? values.filter(v => v.toLowerCase().includes(search.toLowerCase())) : values

  const [pos, setPos] = useState({ top: 0, left: 0 })
  useEffect(() => {
    if (anchorRef) { const r = anchorRef.getBoundingClientRect(); setPos({ top: r.bottom + 2, left: r.left }) }
    setTimeout(() => searchRef.current?.focus(), 50)
  }, [anchorRef])

  const panelId = `dt-filter-${colName}`
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const onClick = (e: MouseEvent) => {
      if (document.getElementById(panelId)?.contains(e.target as Node)) return
      if (anchorRef?.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    const t = setTimeout(() => document.addEventListener('mousedown', onClick), 50)
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose, panelId, anchorRef])

  return createPortal(
    <div id={panelId} className="fixed z-[100] w-48 rounded-lg border border-zinc-200 bg-white shadow-xl dark:border-zinc-700 dark:bg-zinc-800" style={{ top: pos.top, left: pos.left }}>
      <div className="border-b border-zinc-100 p-1.5 dark:border-zinc-700">
        <input ref={searchRef} type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-full rounded border border-zinc-200 px-1.5 py-0.5 text-[10px] focus:border-indigo-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-white" />
      </div>
      <div className="max-h-48 overflow-y-auto p-1">
        {selected.size > 0 && (
          <button onClick={onClear} className="mb-1 w-full rounded px-1.5 py-0.5 text-left text-[10px] text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-500/10">Clear filter</button>
        )}
        {filtered.length === 0 && <p className="px-1 py-2 text-[10px] text-zinc-400">No values</p>}
        {filtered.map(v => (
          <label key={v} className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 text-[10px] text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-700/50">
            <input type="checkbox" checked={selected.has(v)} onChange={() => onToggle(colName, v)} className="h-3 w-3 rounded border-zinc-300 text-indigo-600" />
            <span className="truncate">{v || '(empty)'}</span>
          </label>
        ))}
      </div>
    </div>,
    document.body,
  )
}
